import type { RequestFn } from '../http';

/**
 * Domains aus AutoDNS (v1.409.0): Domains des InterNetX-Kontos, 1× täglich abgeglichen, nur lesen.
 * Backend: services/domains/, routes/domains.ts. Lesen für alle Mitarbeiter, Zuordnung freigeben nur
 * Admin/Teamleitung, Laufstand und „Jetzt abgleichen“ nur Admin (der Server prüft die Rolle).
 */

export type DomainAssignmentStatus = 'UNASSIGNED' | 'SUGGESTED' | 'CONFIRMED' | 'REJECTED' | 'INTERNAL';
export type DomainDecision = 'ACCEPT' | 'REJECT' | 'ASSIGN' | 'INTERNAL' | 'UNASSIGN';
export type DomainDnsHost = 'CLOUDFLARE' | 'AUTODNS' | 'SONSTIGE';

export interface DomainCustomerRef {
  id: string;
  name: string;
  number: string | null;
}

export interface DomainListItem {
  id: string;
  name: string;
  idn: string | null;
  tld: string;
  registrar: string;
  expiresAt: string | null;
  payableAt: string | null;
  expiresInDays: number | null;
  autoRenew: boolean | null;
  autoRenewStatus: string | null;
  renewalWarning: boolean;
  registryStatus: string | null;
  nameServers: string[];
  dnsHost: DomainDnsHost | null;
  contacts: { owner: string | null; admin: string | null; tech: string | null };
  autodnsCreatedAt: string | null;
  autodnsUpdatedAt: string | null;
  presentInAccount: boolean;
  missingSince: string | null;
  lastSyncedAt: string | null;
  dnsZone: { id: string; presentInAccount: boolean } | null;
  assignment: {
    status: DomainAssignmentStatus;
    customer: DomainCustomerRef | null;
    suggestedCustomer: DomainCustomerRef | null;
    suggestedInternal: boolean;
    suggestionReason: string | null;
    suggestionSource: string | null;
    suggestedAt: string | null;
    confirmedByUserId: string | null;
    confirmedAt: string | null;
  };
}

export interface DomainHistoryChange {
  field: 'expiresAt' | 'payableAt' | 'autoRenew' | 'registryStatus' | 'nameServers' | 'presentInAccount' | string;
  before: unknown;
  after: unknown;
}

export interface DomainHistoryEntry {
  id: string;
  changedAt: string;
  changes: DomainHistoryChange[];
}

export interface DomainAssignmentLogRow {
  id: string;
  action: string;
  fromStatus: string | null;
  toStatus: string;
  fromCustomerId: string | null;
  toCustomerId: string | null;
  actorLabel: string | null;
  source: string;
  reason: string | null;
  createdAt: string;
}

export interface DomainDetail {
  domain: DomainListItem;
  history: DomainHistoryEntry[];
  assignmentLog: DomainAssignmentLogRow[];
}

export interface DomainSyncState {
  lastRunAt: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  domainCount: number | null;
  lastRequestCount: number | null;
  running: boolean;
  manualRequestedAt: string | null;
}

export interface DomainFilter {
  customerId?: string;
  status?: DomainAssignmentStatus[];
  unassigned?: boolean;
  dnsHost?: DomainDnsHost;
  expiringDays?: number;
  q?: string;
  missing?: boolean;
}

export const DOMAINS_QUERY_KEY = ['autodns-domains'] as const;
/** Filter „läuft bald ab“ der Liste */
export const EXPIRING_SOON_DAYS = 60;

export function domainQuery(f: DomainFilter): string {
  const p = new URLSearchParams();
  if (f.customerId) p.set('customerId', f.customerId);
  if (f.status?.length) p.set('status', f.status.join(','));
  if (f.unassigned) p.set('unassigned', 'true');
  if (f.dnsHost) p.set('dnsHost', f.dnsHost);
  if (f.expiringDays != null) p.set('expiringDays', String(f.expiringDays));
  if (f.q) p.set('q', f.q);
  if (f.missing) p.set('missing', 'true');
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function createAutodnsDomainsApi(request: RequestFn) {
  return {
    async listDomains(filter: DomainFilter = {}): Promise<{ domains: DomainListItem[] }> {
      return request(`/domains${domainQuery(filter)}`);
    },
    async getDomain(idOrName: string): Promise<DomainDetail> {
      return request(`/domains/${encodeURIComponent(idOrName)}`);
    },
    async getDomainSyncState(): Promise<DomainSyncState> {
      return request('/domains/sync-state');
    },
    async requestDomainSync(): Promise<{ queued: true; requestedAt: string }> {
      return request('/domains/sync', { method: 'POST', body: JSON.stringify({}) });
    },
    async suggestDomainAssignment(id: string, body: { customerId?: string; internal?: true; reason: string }): Promise<{ status: DomainAssignmentStatus }> {
      return request(`/domains/${encodeURIComponent(id)}/assignment-suggestion`, { method: 'POST', body: JSON.stringify(body) });
    },
    async decideDomainAssignment(id: string, body: { action: DomainDecision; customerId?: string; reason?: string }): Promise<{ status: DomainAssignmentStatus; customerId: string | null }> {
      return request(`/domains/${encodeURIComponent(id)}/assignment`, { method: 'POST', body: JSON.stringify(body) });
    },
  };
}

export const DOMAIN_STATUS_LABEL: Record<DomainAssignmentStatus, string> = {
  UNASSIGNED: 'nicht zugeordnet',
  SUGGESTED: 'Vorschlag offen',
  CONFIRMED: 'zugeordnet',
  REJECTED: 'Vorschlag abgelehnt',
  INTERNAL: 'FixByte intern',
};

export const DOMAIN_STATUS_COLOR: Record<DomainAssignmentStatus, string> = {
  UNASSIGNED: 'gray',
  SUGGESTED: 'yellow',
  CONFIRMED: 'green',
  REJECTED: 'red',
  INTERNAL: 'blue',
};

export const DNS_HOST_LABEL: Record<DomainDnsHost, string> = {
  CLOUDFLARE: 'Cloudflare',
  AUTODNS: 'AutoDNS',
  SONSTIGE: 'Sonstige',
};

export function dnsHostLabel(h: DomainDnsHost | null): string {
  return h ? DNS_HOST_LABEL[h] : '—';
}

/** „in 12 Tagen“, „heute“, „seit 3 Tagen abgelaufen“ — `null` ohne Datum. */
export function expiryLabel(days: number | null): string | null {
  if (days == null) return null;
  if (days === 0) return 'heute';
  if (days === 1) return 'morgen';
  if (days > 1) return `in ${days} Tagen`;
  if (days === -1) return 'seit 1 Tag abgelaufen';
  return `seit ${-days} Tagen abgelaufen`;
}

export function autoRenewLabel(d: { autoRenew: boolean | null; autoRenewStatus: string | null }): string {
  if (d.autoRenewStatus === 'ONCE') return 'einmalig';
  if (d.autoRenew === true) return 'ja';
  if (d.autoRenew === false) return 'nein';
  return '—';
}

const HISTORY_FIELD_LABEL: Record<string, string> = {
  expiresAt: 'Ablauf',
  payableAt: 'Bezahlt bis',
  autoRenew: 'Auto-Renew',
  registryStatus: 'Registry-Status',
  nameServers: 'Nameserver',
  presentInAccount: 'Im Konto',
};

const dateFmt = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'medium' });

function historyValue(field: string, v: unknown): string {
  if (v === null || v === undefined) return '—';
  if ((field === 'expiresAt' || field === 'payableAt') && typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : dateFmt.format(d);
  }
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (Array.isArray(v)) return v.length ? v.join(', ') : 'keine';
  return String(v);
}

/** Ein Verlaufseintrag als Text: „Ablauf: 1. März 2027 → 1. März 2028“. */
export function formatHistoryChange(c: DomainHistoryChange): string {
  return `${HISTORY_FIELD_LABEL[c.field] ?? c.field}: ${historyValue(c.field, c.before)} → ${historyValue(c.field, c.after)}`;
}

/** Freigabe-Liste: Domains mit offenem Vorschlag. */
export function hasOpenSuggestion(d: Pick<DomainListItem, 'assignment'>): boolean {
  return !!d.assignment.suggestedCustomer || d.assignment.suggestedInternal;
}

export const SUGGESTION_SOURCE_LABEL: Record<string, string> = {
  KIAN_API: 'von KIAN',
  UI: 'von einem Mitarbeiter',
  DNS_ZONE: 'aus der DNS-Zone',
};
